"use client";

import * as React from "react";
import { Message } from "@/lib/core/crm/types";
import { cn } from "@/lib/utils";
import { Link2, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipTrigger } from "@west-tennessee-consulting/ui";
import {
  MessageHeader,
  MessageMetadata,
  MessageContent,
  MessageActions,
  ContactContextCard,
  MessageRelatedTab,
} from "../components";

interface MessageDetailSidebarProps {
  message: Message;
  onClose: () => void;
  className?: string;
  showActions?: boolean;
  onCopyLink?: () => void;
}

export function MessageDetailSidebar({
  message,
  onClose,
  className,
  showActions = true,
  onCopyLink,
}: MessageDetailSidebarProps) {
  const [activeTab, setActiveTab] = React.useState("message");

  return (
    <div className={cn("flex h-full flex-col min-h-0", className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex-1 min-w-0">
          <MessageHeader message={message} />
        </div>

        {/* Desktop actions */}
        {showActions && (
          <div className="flex items-center space-x-2 ml-4">
            <MessageActions message={message} onCopyLink={onCopyLink} />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close message details sidebar"
                  className="p-2 hover:bg-muted rounded-md"
                >
                  <X className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Close message details sidebar</TooltipContent>
            </Tooltip>
          </div>
        )}

        {!showActions && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close message preview"
                className="p-2 hover:bg-muted rounded-md"
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Close message preview</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Tabs - min-h-0 so content can shrink and scroll independently */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="flex-shrink-0 w-full rounded-none border-b bg-transparent p-0 h-auto">
          <TabsTrigger
            value="message"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
          >
            Message
          </TabsTrigger>
          <TabsTrigger
            value="contact"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
          >
            Contact
          </TabsTrigger>
          <TabsTrigger
            value="related"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary gap-1.5"
          >
            <Link2 className="h-3.5 w-3.5" />
            Related
          </TabsTrigger>
        </TabsList>

        {/* Message Tab - scrolls independently when mouse is over message detail */}
        <TabsContent value="message" className="flex-1 overflow-y-auto min-h-0 p-4 space-y-6 m-0">
          <MessageMetadata message={message} />
          <MessageContent message={message} />
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact" className="flex-1 overflow-y-auto min-h-0 p-4 m-0">
          <ContactContextCard message={message} />
        </TabsContent>

        {/* Related Tab */}
        <TabsContent value="related" className="flex-1 overflow-y-auto min-h-0 p-4 m-0">
          <MessageRelatedTab message={message} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
